import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Vertical-section-nav row.
 *
 * Active state is a raised paper row (`.sidebar-item-active` in global.css):
 * paper fill + `--sh-1`, ink-900 label at weight 600, brand-500 icon. Inactive
 * is ink-600 text + ink-400 icon on transparent; hover fills ink-100.
 *
 * `collapsed` hides the label and centers the icon for the rail mode.
 * `badge` (e.g. "Coming Soon") sits after the label when expanded, and on the icon's
 * top-right corner when collapsed — where only ~30px is clear, so anything longer than
 * about five characters must supply `badgeShort` (e.g. "Soon") for that corner.
 * Without it a long badge is ellipsised to nonsense ("Comin…"). The full `badge`
 * still reaches the user collapsed: it stays in the accessible name, and
 * `Sidebar` puts it in the row's tooltip. `count` (e.g. unread notifications) is a
 * neutral pill when expanded and a brand dot on the icon when collapsed — the
 * number itself is in the tooltip/accessible name there.
 *
 * Extra button props (ref, handlers) are forwarded so the row can sit under a
 * Radix `TooltipTrigger asChild` in the collapsed rail.
 */
export function SidebarItem({
  icon,
  label,
  count,
  badge,
  badgeShort,
  active,
  collapsed,
  onClick,
  className,
  ...rest
}: Omit<React.ComponentPropsWithRef<'button'>, 'children' | 'onClick'> & {
  icon?: React.ReactNode;
  label: string;
  count?: string | number;
  badge?: string;
  /** Short form of `badge` for the collapsed rail's corner pill (see doc above). */
  badgeShort?: string;
  active?: boolean;
  collapsed?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}) {
  const hasCount = count !== undefined && count !== 0 && count !== '';
  const accessibleName = [label, badge, hasCount ? String(count) : null]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      // `rest` first: when wrapped in a Radix `TooltipTrigger asChild`, Slot
      // merges its ref + pointer/focus handlers in through here.
      {...rest}
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? accessibleName : undefined}
      className={cn(
        'sidebar-item',
        active && 'sidebar-item-active',
        collapsed && 'sidebar-item-collapsed',
        className,
      )}
    >
      {icon && (
        <span className="sidebar-item-icon" aria-hidden>
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: collapsed ? 15 : 17 })
            : icon}
          {collapsed && badge && (
            <span className="sidebar-item-pill sidebar-item-pill-corner is-beta">{badgeShort ?? badge}</span>
          )}
          {collapsed && !badge && hasCount && <span className="sidebar-item-dot" />}
        </span>
      )}
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && badge && <span className="sidebar-item-pill is-beta">{badge}</span>}
      {!collapsed && hasCount && <span className="sidebar-item-pill num">{count}</span>}
    </button>
  );
}
